import {
  buildWorkspacePath,
  DEFAULT_WORKSPACE_ID,
  parseWorkspaceRoute,
} from "@/utils/workspaceRoutes"

interface DrillDownLinkRow {
  run_id: string
  simulation_output_id: string
}

/**
 * Generate shareable link URL for a single simulation
 */
export function getShareableLinkUrl(
  runId: string,
  simulationOutputId: string,
): string {
  const baseUrl = window.location.origin
  return `${baseUrl}/shared/runs/${runId}/results/${simulationOutputId}`
}

/**
 * Generate internal link URL for a single simulation
 */
export function getInternalLinkUrl(
  slug: string,
  runId: string,
  simulationOutputId: string,
): string {
  const parsed = parseWorkspaceRoute(window.location.pathname)
  const workspaceId =
    parsed?.organizationSlug === slug
      ? parsed.workspaceId
      : DEFAULT_WORKSPACE_ID
  const baseUrl = window.location.origin
  const path = buildWorkspacePath(
    slug,
    workspaceId,
    `/simulated/runs/${runId}/results/${simulationOutputId}`,
  )
  return `${baseUrl}${path}`
}

/**
 * Generate shareable links for all simulations
 * Returns a map of simulationOutputId to shareable URL
 * Note: This function only generates URLs. The caller must make simulations public.
 */
export function generateShareableLinks(
  rows: DrillDownLinkRow[],
): Map<string, string> {
  const linkMap = new Map<string, string>()

  for (const row of rows) {
    const shareableLink = getShareableLinkUrl(
      row.run_id,
      row.simulation_output_id,
    )
    linkMap.set(row.simulation_output_id, shareableLink)
  }

  return linkMap
}

/**
 * Generate internal links for all simulations
 * Returns a map of simulationOutputId to internal URL
 */
export function generateInternalLinks(
  rows: DrillDownLinkRow[],
  slug: string,
): Map<string, string> {
  const linkMap = new Map<string, string>()

  for (const row of rows) {
    const internalLink = getInternalLinkUrl(
      slug,
      row.run_id,
      row.simulation_output_id,
    )
    linkMap.set(row.simulation_output_id, internalLink)
  }

  return linkMap
}
